import { useContext, useEffect, useMemo, useRef } from "react";

import { useTheme } from "@emotion/react";
import { Box, Typography } from "@mui/material";
import vegaEmbed from "vega-embed";

import { AppContext } from "../../../App";
import useDebtScheduleAmortizationDebtServiceChart from "../hooks/useDebtScheduleAmortizationDebtServiceChart";

/**
 * Debt Service Stacked Bar Chart Component
 * Uses vega-embed directly to create vertical stacked bars showing principal, interest, and extra principal
 * @param {Object} props - Component props
 * @param {string} props.debt_schedule_id - The debt schedule ID to display
 */
export const DebtServiceStackedBarChart = ({ debt_schedule_id }) => {
  const { globalState } = useContext(AppContext);
  const theme = useTheme();
  const chartRef = useRef(null);
  const viewRef = useRef(null);

  const { chartData } = useDebtScheduleAmortizationDebtServiceChart(debt_schedule_id, globalState);

  // Create Vega-Lite spec for vertical stacked bars
  const vegaSpec = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    // Calculate today's date and find the next payment date
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today

    // Find the first future payment date or use today
    const futureDates = chartData
      .map((d) => new Date(d.payment_date))
      .filter((d) => d >= today)
      .sort((a, b) => a - b);

    const startDate = futureDates.length > 0 ? futureDates[0] : today;

    // Filter chart data to only include future payments
    const filteredChartData = chartData.filter((d) => new Date(d.payment_date) >= startDate);

    return {
      $schema: "https://vega.github.io/schema/vega-lite/v6.json",
      title: {
        text: "Debt Service",
        color: theme.palette.text.primary,
        offset: 20,
        fontSize: 18,
        fontWeight: 500,
      },
      data: {
        values: filteredChartData,
      },
      config: {
        background: "transparent",
        view: { stroke: null },
        axis: {
          labelFontSize: 14,
          titleFontSize: 16,
        },
      },
      mark: {
        type: "bar",
        cornerRadiusTopLeft: 4,
        cornerRadiusTopRight: 4,
        width: { band: 0.5 },
      },
      encoding: {
        x: {
          field: "payment_date",
          type: "temporal",
          title: "Payment Date",
          axis: {
            format: "%b %Y",
            labelAngle: -45,
            labelFontSize: 14,
            titleFontSize: 16,
          },
        },
        y: {
          field: "dataValueY_mean",
          type: "quantitative",
          title: "Amount",
          stack: "zero",
          axis: {
            format: ",.0f",
            formatType: "number",
            labelExpr: "datum.value >= 1000 ? datum.value / 1000 + ' k' : datum.value",
            labelFontSize: 14,
            titleFontSize: 16,
          },
        },
        color: {
          field: "category",
          type: "nominal",
          scale: {
            domain: ["Principal Balance", "Principal", "Interest", "Extra Principal"],
            range: [
              "#B3E5FC", // Principal Balance - Light Light Blue
              theme.palette.primary.main || "#1976d2", // Principal - Blue
              theme.palette.warning.main || "#ed6c02", // Interest - Orange
              theme.palette.success.main || "#2e7d32", // Extra Principal - Green
            ],
          },
          title: "",
          legend: {
            orient: "bottom",
            direction: "horizontal",
            labelFontSize: 14,
            titleFontSize: 16,
          },
        },
        tooltip: [
          { field: "payment_date", type: "temporal", title: "Payment Date", format: "%m/%d/%Y" },
          { field: "category", type: "nominal", title: "Category" },
          {
            field: "dataValueY_mean",
            type: "quantitative",
            title: "Value",
            format: ",.2f",
          },
        ],
        opacity: {
          condition: { selection: "hover", value: 0.8 },
          value: 1.0,
        },
      },
      selection: {
        hover: { type: "single", on: "mouseover", empty: "none" },
      },
      width: "container",
      height: 400,
      autosize: { type: "fit", contains: "padding" },
    };
  }, [chartData, theme]);

  // Render Vega chart
  useEffect(() => {
    if (!chartRef.current || !vegaSpec) return;

    // Clean up previous view
    if (viewRef.current) {
      viewRef.current.finalize();
      viewRef.current = null;
    }

    vegaEmbed(chartRef.current, vegaSpec, {
      actions: false,
      renderer: "svg",
    })
      .then((result) => {
        viewRef.current = result.view;
      })
      .catch((error) => {
        console.error("Error rendering Vega chart:", error);
      });

    return () => {
      if (viewRef.current) {
        viewRef.current.finalize();
        viewRef.current = null;
      }
    };
  }, [vegaSpec]);

  // Don't render if no debt_schedule_id
  if (!debt_schedule_id || String(debt_schedule_id) === "0") {
    return null;
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Box sx={{ mb: 3, p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          No amortization data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <div ref={chartRef} style={{ width: "100%" }} />
    </Box>
  );
};
